// src/PublicBioPageMobile.js
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet';
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Container,
  Fade,
  Zoom,
  Slide,
  Chip,
  Card,
  CardContent,
  Avatar,
  Stack,
  AppBar,
  Fab,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material';
import { ThemeProvider, alpha } from '@mui/material/styles';
import { styled, keyframes } from '@mui/material/styles';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QRCode from 'react-qr-code';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import AnimationIcon from '@mui/icons-material/Animation';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import ShareIcon from '@mui/icons-material/Share';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import LaunchIcon from '@mui/icons-material/Launch';

import TikTokIcon from '../Components/Icons/TikTok';
import TwitchIcon from '../Components/Icons/Twitch';
import CastIcon from '@mui/icons-material/Cast';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

import API from '../API';
import HomeHeader from '../Components/HomeHeader';
import { lightTheme } from '../Utils/baseTheme';
import defaultPfp from '../Components/globalAssets/ppfLogo.png';

/*********************************************************************
 * Mobile-Optimized Animations
 *********************************************************************/
const fadeInMobile = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(15px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const slideUpMobile = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(25px) scale(0.98); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

const gentleFloat = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

const shimmerMobile = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

/*********************************************************************
 * Mobile-Specific Styled Components
 *********************************************************************/
const MobileAnimatedWrapper = styled('div')(({ theme, animationType, animationsEnabled, delay = 0 }) => {
  if (!animationsEnabled) return {};
  
  const animationMap = {
    'FADE_IN': `${fadeInMobile} 0.6s ease-out ${delay}ms both`,
    'SLIDE_UP': `${slideUpMobile} 0.5s ease-out ${delay}ms both`,
  };

  return {
    animation: animationMap[animationType] || animationMap['FADE_IN'],
  };
});

const MobileHeroSection = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const MobileHeroBackground = styled('div')(({ imageUrl }) => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  zIndex: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)',
  },
}));

const MobileHeroVideo = styled('video')(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  minWidth: '100%',
  minHeight: '100%',
  width: 'auto',
  height: 'auto',
  transform: 'translate(-50%, -50%)',
  objectFit: 'cover',
  zIndex: 0,
}));

const MobileHeroContent = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: '#fff',
  padding: theme.spacing(3),
  maxWidth: '100%',
}));

const MobileAvatar = styled(Avatar)(({ theme, animationsEnabled }) => ({
  width: 100,
  height: 100,
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  border: '3px solid rgba(255, 255, 255, 0.3)',
  animation: animationsEnabled ? `${gentleFloat} 4s ease-in-out infinite` : 'none',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
}));

const MobileTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: 'clamp(1.8rem, 6vw, 2.5rem)',
  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.9) 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(1),
  lineHeight: 1.2,
}));

const MobileSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(0.9rem, 3vw, 1.1rem)',
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 400,
  lineHeight: 1.5,
  marginBottom: theme.spacing(3),
}));

const MobileActionButton = styled(Button)(({ theme, variant = 'primary' }) => {
  const variants = {
    primary: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
    },
    secondary: {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#fff',
    },
    ghost: {
      background: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      color: '#fff',
    }
  };

  return {
    ...variants[variant],
    borderRadius: '25px',
    padding: '12px 24px',
    fontWeight: 600,
    fontSize: '0.9rem',
    textTransform: 'none',
    minWidth: 120,
    boxShadow: variant !== 'ghost' ? '0 4px 20px rgba(102, 126, 234, 0.3)' : 'none',
    transition: 'all 0.2s ease',
    '&:active': {
      transform: 'scale(0.98)',
    },
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: variant !== 'ghost' ? '0 6px 25px rgba(102, 126, 234, 0.4)' : '0 4px 15px rgba(0, 0, 0, 0.2)',
    },
  };
});

const MobileSocialGrid = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  margin: theme.spacing(3, 0),
}));

const MobileSocialIcon = styled(IconButton)(({ theme }) => ({
  width: 50,
  height: 50,
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#fff',
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  '&:active': {
    transform: 'scale(0.95)',
    background: 'rgba(255, 255, 255, 0.2)',
  },
}));

const MobileContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0),
  background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
  minHeight: '50vh',
  paddingBottom: `calc(98px + env(safe-area-inset-bottom, 0px) + ${theme.spacing(3)})` // AppShell aware
}));

const MobileCard = styled(Card)(({ theme, animationsEnabled }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  margin: theme.spacing(1, 0),
  transition: 'all 0.2s ease',
  background: '#fff',
  '&:active': {
    transform: animationsEnabled ? 'scale(0.98)' : 'scale(0.99)',
    boxShadow: '0 2px 15px rgba(0, 0, 0, 0.15)',
  },
}));

const MobileItemCard = styled(MobileCard)(({ theme, animationsEnabled }) => ({
  position: 'relative',
  cursor: 'pointer',
  '&:hover': {
    transform: animationsEnabled ? 'translateY(-2px)' : 'translateY(-1px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
    '& .mobile-item-overlay': {
      opacity: 1,
    },
  },
}));

const MobileItemMedia = styled('div')(() => ({
  width: '100%',
  height: '150px',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
}));

const MobileItemOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
  backdropFilter: 'blur(5px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  transition: 'opacity 0.2s ease',
  textAlign: 'center',
  padding: theme.spacing(2),
  color: '#fff',
}));

const MobileIndicatorBar = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 64, // Below header
  zIndex: 100,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  padding: theme.spacing(1, 2),
}));

const FloatingQRButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: `calc(98px + env(safe-area-inset-bottom, 0px) + ${theme.spacing(2)})`, // AppShell aware
  right: theme.spacing(2),
  zIndex: 1000,
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
  },
}));

const MobileDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '20px 20px 0 0',
    margin: 0,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    maxWidth: '100%',
    maxHeight: `calc(90vh - 98px - env(safe-area-inset-bottom, 0px))`, // AppShell aware
    paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + ${theme.spacing(1)})` // Safe area padding
  },
}));

/******************** vCard builder ************************/
function buildVCard(contact) {
  let lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${contact.full_name}`
  ];
  if (contact.first_name || contact.last_name) {
    lines.push(
      `N:${contact.last_name || ''};${contact.first_name || ''};${contact.middle_name || ''};${contact.nickname || ''}`
    );
  }
  if (contact.email) lines.push(`EMAIL;TYPE=INTERNET:${contact.email}`);
  if (contact.phone) lines.push(`TEL;TYPE=CELL:${contact.phone}`);
  if (contact.company) lines.push(`ORG:${contact.company}`);
  if (contact.title) lines.push(`TITLE:${contact.title}`);
  lines.push('END:VCARD');
  return lines.join('\n');
}

export default function PublicBioPageMobile() {
  const { slug } = useParams();
  const [qrOpen, setQrOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  /* ─────────────────── Fetch bio page first ─────────────────── */
  const { data: bioPage, isLoading: bioLoading, error: bioErr } = useQuery(
    ['bioPage', slug],
    () => API.bio.getPublic(slug),
    { enabled: !!slug, refetchOnWindowFocus: false }
  );
  
  // Only fetch creator data if the bio page belongs to a creator (has username field)
  const shouldFetchCreator = bioPage?.username;
  
  const { data: creatorResp, isLoading: creatorLoading, error: creErr } = useQuery(
    ['creatorInfo', bioPage?.username],
    () => API.creators.fetchDetails(bioPage.username),
    { 
      enabled: !!shouldFetchCreator, 
      refetchOnWindowFocus: false 
    }
  );

  const loading = bioLoading || (shouldFetchCreator && creatorLoading);
  const error = bioErr || (shouldFetchCreator && creErr);

  if (loading) {
    return (
      <Backdrop open sx={{ zIndex: 9999, background: 'rgba(0,0,0,0.8)' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={50} sx={{ color: '#667eea' }} />
          <Typography variant="body1" color="#fff">Loading...</Typography>
        </Box>
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Box p={3} textAlign="center">
        <Typography variant="h5" color="error" gutterBottom>
          Something went wrong
        </Typography>
        <Typography color="text.secondary">
          {error.message || 'Error loading data.'}
        </Typography>
      </Box>
    );
  }

  if (!bioPage) {
    return (
      <Box p={3} textAlign="center" minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <Box>
          <Typography variant="h5" gutterBottom>Page Not Found</Typography>
          <Typography color="text.secondary">This page does not exist.</Typography>
        </Box>
      </Box>
    );
  }

  /* ───────────── Extract enhanced page info ───────────── */
  const { 
    title, 
    description, 
    header_media_url, 
    items = [], 
    qr_enabled, 
    self_contact,
    page_type,
    view_type,
    animations_enabled = true,
    animation_type = 'FADE_IN',
    link_user_page,
    linked_page_url
  } = bioPage;
  
  // Handle both creator and user bio pages
  let displayInfo = {
    display_name: title || slug,
    pfphref: defaultPfp,
    tiktok_link: null,
    instagram_link: null,
    youtube_link: null,
    twitter_link: null,
    twitch_link: null,
    snap_link: null,
    x_link: null,
    kick_link: null,
    podcast_link: null,
    creator: null
  };

  // If this is a creator's bio page and we have creator data
  if (creatorResp?.creator) {
    const creator = creatorResp.creator;
    displayInfo = {
      display_name: title || creator.display_name || creator.creator,
      pfphref: creator.pfphref || defaultPfp,
      tiktok_link: creator.tiktok_link,
      instagram_link: creator.instagram_link,
      youtube_link: creator.youtube_link,
      twitter_link: creator.twitter_link,
      twitch_link: creator.twitch_link,
      snap_link: creator.snap_link,
      x_link: creator.x_link,
      kick_link: creator.kick_link,
      podcast_link: creator.podcast_link,
      creator: creator.creator
    };
  }

  const isHeaderImage = header_media_url && /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(header_media_url);
  const sorted = [...items].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

  /* ───────────── Animation Helper ───────────── */
  const getAnimationDelay = (index) => animations_enabled ? index * 100 : 0;

  /* ───────────── Mobile Share Function ───────────── */
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: displayInfo.display_name,
          text: description || `Check out ${displayInfo.display_name}'s bio page`,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Error sharing:', err);
        // Fallback to copying URL
        navigator.clipboard.writeText(window.location.href);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(window.location.href);
    }
  };

  /* ───────────── Mobile Render Helper ───────────── */
  const renderMobileItem = (item, index) => {
    const getGridSize = (size) => {
      // Mobile uses simpler grid sizing
      switch (size) {
        case 'LARGE': return { xs: 12 };
        case 'MEDIUM': return { xs: 12, sm: 6 };
        case 'SMALL': return { xs: 12, sm: 6 };
        default: return { xs: 12, sm: 6 };
      }
    };

    if (item.component_type === 'VIDEO_EMBED' && item.url) {
      return (
        <Grid item {...getGridSize(item.size)} key={item.id}>
          <MobileAnimatedWrapper
            animationType={animation_type}
            animationsEnabled={animations_enabled}
            delay={getAnimationDelay(index)}
          >
            <MobileItemCard 
              onClick={() => window.open(item.url, '_blank')}
              animationsEnabled={animations_enabled}
            >
              <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                <iframe
                  title={item.label || 'video'}
                  src={item.url}
                  width="100%"
                  height="100%"
                  style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    borderRadius: '16px 16px 0 0'
                  }}
                  allowFullScreen
                />
              </Box>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {item.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  {item.description}
                </Typography>
              </CardContent>
            </MobileItemCard>
          </MobileAnimatedWrapper>
        </Grid>
      );
    }

    return (
      <Grid item {...getGridSize(item.size)} key={item.id}>
        <MobileAnimatedWrapper
          animationType={animation_type}
          animationsEnabled={animations_enabled}
          delay={getAnimationDelay(index)}
        >
          <MobileItemCard 
            onClick={() => item.url && window.open(item.url, '_blank')}
            animationsEnabled={animations_enabled}
          >
            <MobileItemMedia>
              {item.media_url ? (
                /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.media_url) ? (
                  <Box
                    component="img"
                    src={item.media_url}
                    alt={item.label || 'img'}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                ) : (
                  <video
                    src={item.media_url}
                    autoPlay
                    loop
                    muted
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                )
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'text.secondary'
                  }}
                >
                  <Typography variant="caption">No Media</Typography>
                </Box>
              )}
              <MobileItemOverlay className="mobile-item-overlay">
                {item.label && (
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                )}
                {item.description && (
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9, fontSize: '0.85rem' }}>
                    {item.description}
                  </Typography>
                )}
              </MobileItemOverlay>
            </MobileItemMedia>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {item.label || 'Untitled'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {item.description}
              </Typography>
              {item.url && (
                <Box display="flex" alignItems="center" mt={1}>
                  <LaunchIcon sx={{ fontSize: 14, color: 'primary.main', mr: 0.5 }} />
                  <Typography variant="caption" color="primary">
                    Open Link
                  </Typography>
                </Box>
              )}
            </CardContent>
          </MobileItemCard>
        </MobileAnimatedWrapper>
      </Grid>
    );
  };

  /* ───────────── vCard + QR helpers ───────────── */
const vcardStr = self_contact ? buildVCard(self_contact) : '';
const vcardData = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcardStr)}`;

  const socialLinks = [
    { link: displayInfo.tiktok_link, icon: <TikTokIcon />, name: 'TikTok' },
    { link: displayInfo.instagram_link, icon: <InstagramIcon />, name: 'Instagram' },
    { link: displayInfo.youtube_link, icon: <YouTubeIcon />, name: 'YouTube' },
    { link: displayInfo.twitter_link, icon: <TwitterIcon />, name: 'Twitter' },
    { link: displayInfo.twitch_link, icon: <TwitchIcon />, name: 'Twitch' },
    { link: displayInfo.snap_link, icon: <LinkIcon />, name: 'Snapchat' },
    { link: displayInfo.x_link, icon: <TwitterIcon />, name: 'X' },
    { link: displayInfo.kick_link, icon: <CastIcon />, name: 'Kick' },
    { link: displayInfo.podcast_link, icon: <PodcastsIcon />, name: 'Podcast' },
  ].filter(social => social.link);

  return (
    <>
      <Helmet>
        <title>{displayInfo.display_name}</title>
        <meta name="description" content={description || `Bio page for ${displayInfo.display_name}`} />
      </Helmet>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Toolbar />        

        {/* ───────────── MOBILE HERO SECTION ───────────── */}
        <MobileHeroSection>
          {header_media_url ? (
            isHeaderImage ? (
              <MobileHeroBackground imageUrl={header_media_url} />
            ) : (
              <>
                <MobileHeroVideo src={header_media_url} autoPlay muted loop />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.7) 100%)',
                    zIndex: 1
                  }}
                />
              </>
            )
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            />
          )}

          <MobileHeroContent>
            <MobileAnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={100}
            >
              <Box>
                {!isHeaderImage && (
                  <MobileAvatar 
                    src={displayInfo.pfphref} 
                    alt={displayInfo.display_name}
                    animationsEnabled={animations_enabled}
                  />
                )}
                <MobileTitle variant="h4">
                  {displayInfo.display_name}
                </MobileTitle>
                {description && (
                  <MobileSubtitle variant="body1">
                    {description}
                  </MobileSubtitle>
                )}
              </Box>
            </MobileAnimatedWrapper>

            {/* Social Icons - Mobile Grid Layout */}
            {socialLinks.length > 0 && (
              <MobileAnimatedWrapper
                animationType={animation_type}
                animationsEnabled={animations_enabled}
                delay={200}
              >
                <MobileSocialGrid>
                  {socialLinks.map((social, index) => (
                    <Tooltip title={social.name} key={social.name} placement="top">
                      <MobileSocialIcon 
                        onClick={() => window.open(social.link, '_blank')}
                        size="small"
                      >
                        {social.icon}
                      </MobileSocialIcon>
                    </Tooltip>
                  ))}
                </MobileSocialGrid>
              </MobileAnimatedWrapper>
            )}

            {/* Action Buttons - Mobile Stack */}
            <MobileAnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={300}
            >
              <Stack spacing={2} alignItems="center" sx={{ mt: 2 }}>
                {/* Show "Let's Chat" button for creators */}
                {displayInfo.creator && (
                  <MobileActionButton
                    variant="primary"
                    onClick={() => window.open(`/creators/${displayInfo.creator}`, '_self')}
                    fullWidth
                    sx={{ maxWidth: 200 }}
                  >
                    Let's Chat
                  </MobileActionButton>
                )}

                {/* Show linked page button for profile pages */}
                {page_type === 'PROFILE' && link_user_page && linked_page_url && (
                  <MobileActionButton
                    variant="secondary"
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(linked_page_url, '_self')}
                    fullWidth
                    sx={{ maxWidth: 200 }}
                  >
                    View My {bioPage.username ? 'Mediakit' : 'Profile'}
                  </MobileActionButton>
                )}

                <Stack direction="row" spacing={2}>
                  <MobileActionButton
                    variant="ghost"
                    startIcon={<ShareIcon />}
                    onClick={handleShare}
                    size="small"
                  >
                    Share
                  </MobileActionButton>
                  
                  {qr_enabled && self_contact && (
                    <MobileActionButton
                      variant="ghost"
                      startIcon={<ContactPageIcon />}
                      onClick={() => setQrOpen(true)}
                      size="small"
                    >
                      Contact
                    </MobileActionButton>
                  )}
                </Stack>
              </Stack>
            </MobileAnimatedWrapper>
          </MobileHeroContent>
        </MobileHeroSection>

        {/* ───────────── MOBILE CONTENT SECTION ───────────── */}
        <MobileContentSection>
          <Container maxWidth="sm" sx={{ px: 2 }}>
            <MobileAnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={400}
            >
              <MobileCard sx={{ p: 3, mb: 3, textAlign: 'center' }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Links & Content
                </Typography>
              </MobileCard>
            </MobileAnimatedWrapper>
            
            <Grid container spacing={2}>
              {sorted.map((item, index) => renderMobileItem(item, index))}
            </Grid>

            {/* Mobile Watermark */}
            <MobileAnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={500 + sorted.length * 100}
            >
              <Box sx={{ textAlign: 'center', mt: 4, mb: 2, opacity: 0.6 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Powered by Blitz ✨
                </Typography>
              </Box>
            </MobileAnimatedWrapper>
          </Container>
        </MobileContentSection>

        {/* ───────────── MOBILE QR CODE DIALOG ───────────── */}
        <MobileDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          fullWidth
        >
          <AppBar sx={{ position: 'relative', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Toolbar>
              <Typography sx={{ flex: 1 }} variant="h6">
                Save My Contact
              </Typography>
              <IconButton edge="end" color="inherit" onClick={() => setQrOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <DialogContent sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Scan with your phone's camera to add me to your contacts
            </Typography>
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <QRCode
                value={vcardData}
                size={200}
                style={{
                  height: 'auto',
                  maxWidth: '100%',
                  width: '100%',
                }}
                viewBox="0 0 256 256"
              />
            </Paper>
            <MobileActionButton
                variant="primary"
                startIcon={<GetAppIcon />}
                href={vcardData}
                download={`${self_contact?.full_name || 'contact'}.vcf`}
                fullWidth
                >
                Download Contact Card
                </MobileActionButton>
          </DialogContent>
        </MobileDialog>
      </ThemeProvider>
    </>
  );
}